/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / CUSTOMER-SCHEDULE — Mitarbeiter-Seite Dienstplan (S262 Cut A)
   =============================================================================
   Route `#dienstplan/<token>` (public, ohne Login — Muster #newsletter/#tisch).
   Der Mitarbeiter bekommt den Link per Mail (Kind schedule_request /
   schedule_reminder), trägt am Handy für sieben Tage je EINE Wahl ein und
   sendet ab. Bis zur Deadline zeigt derselbe Link die gespeicherte Auswahl und
   Absenden überschreibt; danach ist die Ansicht gesperrt.

   Was diese Datei bewusst NICHT tut:
   1. Sie zeigt keine fremden Daten. `schedule_get_request` liefert nur den
      eigenen Datensatz (Spec § 8), und die Seite fragt nichts anderes ab.
   2. Sie braucht keinen Auth-Kontext: nur die zwei anon-RPCs, kein
      `getCurrentRestaurantId()`, kein Operator-Layer.
   3. Sie rendert nicht destruktiv: ein Klick auf eine Pill patcht die Zeile
      und den Absenden-Knopf, nicht die ganze Karte (Memory
      feedback_gg_non_destructive_render).

   Prefetch-sicher wie customer-cancel/-newsletter: der GET-Pfad liest nur.
   Die Mutation (Absenden) läuft ausschließlich auf Klick.
============================================================================ */

import { htmlEscape, showToast } from '../utils/helpers.js';
import {
  CHOICE_CODES, CHOICE_LABELS, isValidChoice, weekDays, isoWeekNumber,
  formatDayShort, formatWeekRange, formatDeadline, missingDays, isComplete,
  buildSubmitPayload, choicesFromDays,
} from '../core/schedule-shifts.js';

let _token   = null;
let _data    = null;   // Antwort von schedule_get_request (state 'ok')
let _choices = {};     // { 'YYYY-MM-DD': code }
let _saving  = false;

function supa() {
  return (typeof window !== 'undefined' && window.App && window.App.supabase) || null;
}

function _card() { return document.getElementById('schedule-card'); }

/** Pure — Token aus dem Hash. `#dienstplan/<token>` → token, sonst null. */
export function _parseScheduleHash(hash) {
  if (typeof hash !== 'string') return null;
  const m = hash.match(/^#dienstplan\/(.+)$/);
  if (!m) return null;
  try {
    const t = decodeURIComponent(m[1]).trim();
    return t.length ? t : null;
  } catch (_) { return null; }
}

/** Pure — Fehlercode aus einer RPC-Fehlermeldung (`SCHEDULE_LOCKED: …`). */
export function _errorCode(err) {
  const msg = (err && (err.message || err.details || err.hint)) || (typeof err === 'string' ? err : '');
  const m = String(msg || '').match(/^\s*([A-Z_]{4,})\s*:/);
  return m ? m[1] : null;
}

/** Pure — die Tage der Woche aus der Antwort (Server-Liste vor lokaler Ableitung). */
export function _daysOf(data) {
  const fromServer = (data && Array.isArray(data.days))
    ? data.days.map(r => r && r.day).filter(d => typeof d === 'string')
    : [];
  if (fromServer.length === 7) return fromServer;
  return weekDays(data && data.week_start);
}

/** Pure — eine Tageszeile mit fünf Pills. */
export function _rowHtml(day, choice, locked) {
  const pills = CHOICE_CODES.map(code => {
    const on = choice === code;
    if (locked) {
      return on
        ? `<span class="sch-pill is-on is-static">${htmlEscape(CHOICE_LABELS[code])}</span>`
        : '';
    }
    return `<button type="button" class="sch-pill${on ? ' is-on' : ''}" data-choice="${code}"
              aria-pressed="${on ? 'true' : 'false'}"
              onclick="scheduleChoose('${htmlEscape(day)}', '${code}')">${htmlEscape(CHOICE_LABELS[code])}</button>`;
  }).join('');
  const empty = locked && !isValidChoice(choice)
    ? '<span class="sch-pill is-static is-empty">keine Angabe</span>' : '';
  return `
    <div class="sch-row" data-sch-day="${htmlEscape(day)}">
      <div class="sch-day">${htmlEscape(formatDayShort(day))}</div>
      <div class="sch-pills">${pills}${empty}</div>
    </div>`;
}

export function _rowsHtml(days, choices, locked) {
  return (Array.isArray(days) ? days : []).map(d => _rowHtml(d, (choices || {})[d], locked)).join('');
}

/** Pure — Text am Absenden-Knopf / Hinweis darunter. */
export function _submitHint(days, choices) {
  const n = missingDays(days, choices).length;
  if (n === 0) return 'Alle 7 Tage ausgefüllt — du kannst absenden.';
  return n === 1 ? 'Noch 1 Tag offen.' : `Noch ${n} Tage offen.`;
}

/** Wenn ein Dienstplan-Hash anliegt: View wechseln + laden (rein lesend). */
export function checkScheduleRequestFromUrl() {
  const token = _parseScheduleHash(window.location.hash);
  if (!token) return false;
  _token = token;
  _data = null;
  _choices = {};
  if (typeof window.switchView === 'function') window.switchView('schedule');
  const wrap = _card();
  if (wrap) wrap.innerHTML = _loadingCard();
  loadScheduleRequest().catch(() => {});
  return true;
}

function _loadingCard() {
  return `<h2>Dienstplan</h2><p class="hint">Deine Woche wird geladen …</p>`;
}

function _unknownCard() {
  return `
    <h2>Link nicht gültig</h2>
    <p>Dieser Dienstplan-Link ist nicht (mehr) gültig. Bitte nimm den Link aus der
       aktuellsten Mail — oder frag kurz im Team nach.</p>`;
}

function _errorCard(msg) {
  return `
    <h2>Dienstplan</h2>
    <p style="color:var(--danger);">${htmlEscape(msg || 'Das hat gerade nicht geklappt.')}</p>
    <button class="btn btn-secondary" onclick="loadScheduleRequest()">Erneut laden</button>`;
}

/** Lädt den eigenen Datensatz und rendert die Karte einmal komplett. */
export async function loadScheduleRequest() {
  const client = supa();
  const wrap = _card();
  if (!client || !_token) {
    if (wrap) wrap.innerHTML = _errorCard('Verbindung nicht bereit.');
    return;
  }
  try {
    const { data, error } = await client.rpc('schedule_get_request', { p_token: _token });
    if (error) throw error;
    if (!data || data.state !== 'ok') {
      _data = null;
      if (wrap) wrap.innerHTML = _unknownCard();
      return;
    }
    _data = data;
    _choices = choicesFromDays(data.days);
    renderScheduleView();
  } catch (e) {
    const code = _errorCode(e);
    console.warn('[schedule] schedule_get_request:', code || (e && e.message));
    if (wrap) wrap.innerHTML = _errorCard(code === 'RATE_LIMIT'
      ? 'Zu viele Anfragen — bitte einen Moment warten.'
      : 'Das hat gerade nicht geklappt.');
  }
}

/** Voll-Render der Karte (nur nach Laden / Sperre — Klicks patchen). */
export function renderScheduleView() {
  const wrap = _card();
  if (!wrap || !_data) return;
  const d = _data;
  const days = _daysOf(d);
  const locked = d.locked === true;
  const weekNo = d.week_no || isoWeekNumber(d.week_start);
  const range = formatWeekRange(d.week_start);
  const deadline = formatDeadline(d.deadline_at);
  const name = String(d.name || '').trim();

  const head = `
    <h2>Deine Verfügbarkeit · KW ${htmlEscape(String(weekNo))}</h2>
    <p class="sch-sub">${name ? 'Hallo ' + htmlEscape(name) + ' — ' : ''}${htmlEscape(range)}</p>`;

  let status;
  if (locked) {
    status = `<div class="sch-banner is-locked">🔒 Die Abgabefrist ist vorbei${deadline ? ' (' + htmlEscape(deadline) + ')' : ''}.
      ${d.submitted_at ? 'Deine Angaben sind gespeichert — Änderungen bitte direkt mit dem Team klären.' : 'Du hast nichts eingetragen — für diese Woche gibt es damit keine Schichten.'}</div>`;
  } else if (d.submitted_at) {
    status = `<div class="sch-banner is-saved" id="sch-flash">✅ Gespeichert — du kannst bis ${htmlEscape(deadline)} noch ändern.</div>`;
  } else {
    status = `<div class="sch-banner" id="sch-flash">Bitte bis <strong>${htmlEscape(deadline)}</strong> eintragen. Wer nichts einträgt, bekommt keine Schichten.</div>`;
  }

  const rows = `<div class="sch-rows">${_rowsHtml(days, _choices, locked)}</div>`;

  const foot = locked ? '' : `
    <div class="sch-foot">
      <button class="btn btn-primary" id="sch-submit" onclick="scheduleSubmit()"
              ${isComplete(days, _choices) ? '' : 'disabled'}>Absenden</button>
      <p class="hint" id="sch-hint">${htmlEscape(_submitHint(days, _choices))}</p>
    </div>`;

  wrap.innerHTML = head + status + rows + foot;
}

/** Pill-Klick: Modul-State + nur die betroffene Zeile + Absenden-Knopf patchen. */
export function scheduleChoose(day, choice) {
  if (!_data || _data.locked === true || !isValidChoice(choice)) return;
  _choices[day] = choice;
  const wrap = _card();
  if (!wrap) return;
  const row = wrap.querySelector(`.sch-row[data-sch-day="${day}"]`);
  if (row) {
    row.querySelectorAll('.sch-pill').forEach(btn => {
      const on = btn.dataset.choice === choice;
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  _syncSubmit();
}

function _syncSubmit() {
  const wrap = _card();
  if (!wrap || !_data) return;
  const days = _daysOf(_data);
  const btn = document.getElementById('sch-submit');
  const hint = document.getElementById('sch-hint');
  if (btn) btn.disabled = _saving || !isComplete(days, _choices);
  if (hint) hint.textContent = _saving ? 'Wird gespeichert …' : _submitHint(days, _choices);
}

/** Absenden — nur auf Klick, überschreibt eine frühere Abgabe. */
export async function scheduleSubmit() {
  if (_saving || !_data || !_token) return;
  const days = _daysOf(_data);
  const payload = buildSubmitPayload(days, _choices);
  if (!payload) { _syncSubmit(); return; }
  const client = supa();
  if (!client) { showToast('Verbindung nicht bereit.', 'error'); return; }
  _saving = true;
  _syncSubmit();
  try {
    const { data, error } = await client.rpc('schedule_submit_availability', {
      p_token: _token, p_choices: payload,
    });
    if (error) throw error;
    if (!data || data.ok !== true) {
      // Neutrale Antwort (unbekanntes Token) — Seite frisch laden, sie zeigt dann die Sperre.
      await loadScheduleRequest();
      return;
    }
    _data.submitted_at = data.submitted_at || new Date().toISOString();
    if (data.deadline_at) _data.deadline_at = data.deadline_at;
    const flash = document.getElementById('sch-flash');
    if (flash) {
      flash.className = 'sch-banner is-saved';
      flash.innerHTML = `✅ Gespeichert — du kannst bis ${htmlEscape(formatDeadline(_data.deadline_at))} noch ändern.`;
    }
    showToast('Verfügbarkeit gespeichert.', 'success');
  } catch (e) {
    const code = _errorCode(e);
    console.warn('[schedule] schedule_submit_availability:', code || (e && e.message));
    if (code === 'SCHEDULE_LOCKED') {
      showToast('Die Abgabefrist ist vorbei.', 'error');
      await loadScheduleRequest();          // rendert die gesperrte Ansicht
      return;
    }
    if (code === 'RATE_LIMIT') {
      showToast('Zu viele Anfragen — bitte einen Moment warten.', 'error');
    } else if (code === 'SCHEDULE_INVALID_CHOICE') {
      showToast('Bitte für jeden Tag eine Auswahl treffen.', 'error');
    } else {
      showToast('Das hat gerade nicht geklappt. Bitte noch einmal versuchen.', 'error');
    }
  } finally {
    _saving = false;
    _syncSubmit();
  }
}

/** Zurück zur Startseite (Hash weg, Customer-View). */
export function closeScheduleView() {
  history.replaceState(null, '', window.location.pathname + window.location.search);
  if (typeof window.switchView === 'function') window.switchView('customer');
}

/** Test-Hook: Modul-State zurücksetzen. */
export function _resetScheduleStateForTests() {
  _token = null; _data = null; _choices = {}; _saving = false;
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    checkScheduleRequestFromUrl,
    loadScheduleRequest,
    renderScheduleView,
    scheduleChoose,
    scheduleSubmit,
    closeScheduleView,
  });
}
